import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { AdminHeader, Drawer, FilterTabs, StatusBadge } from '@/features/admin/ui';
import { useRequireRole } from '@/lib/auth/useRequireRole';
import { adminQueries, auditLog, type Profile } from '@/lib/queries/admin';
import { supabase } from '@/lib/supabase';

// Mirrors the profiles_username_format check constraint (input is lowercased).
const ADMIN_USERNAME_RE = /^[a-z0-9][a-z0-9_-]{2,29}$/;

export const Route = createFileRoute('/admin/users')({
  component: UsersPage,
});

const FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'subscribers', label: 'Subscribers' },
  { value: 'creators', label: 'Contributors' },
  { value: 'admins', label: 'Admins' },
  { value: 'moderators', label: 'Moderators' },
  { value: 'suspended', label: 'Suspended' },
] as const;

const ROLES = ['subscriber', 'creator', 'admin', 'moderator'] as const;
const CREATOR_STATUSES = ['none', 'pending', 'approved', 'rejected', 'suspended'] as const;

function UsersPage() {
  useRequireRole(['admin']); // admin-only; moderators 403 on direct nav (Spec 21)
  const qc = useQueryClient();
  const [filter, setFilter] = useState<(typeof FILTERS)[number]['value']>('all');
  const [search, setSearch] = useState('');
  const [active, setActive] = useState<Profile | null>(null);

  const list = useQuery(adminQueries.users(filter, search));
  const rows = list.data ?? [];

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'users'] });

  return (
    <div className="mx-auto max-w-4xl">
      <AdminHeader kicker="Admin · People" title="Users" />
      <input
        type="search"
        placeholder="Search by username or display name"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="mt-6 w-full max-w-sm rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none"
      />
      <div className="mt-4">
        <FilterTabs options={FILTERS} value={filter} onChange={setFilter} />
      </div>

      {list.isLoading ? (
        <p className="mt-8 text-center font-sans text-sm text-ink/50">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="mt-8 text-center font-sans text-sm text-ink/50">No users.</p>
      ) : (
        <ul className="mt-6 divide-y divide-ink/10">
          {rows.map((u) => (
            <li key={u.id}>
              <button
                type="button"
                onClick={() => setActive(u)}
                className="flex w-full items-center justify-between gap-4 py-3 text-left"
              >
                <span className="min-w-0">
                  <span className="font-serif text-base text-ink">
                    {u.display_name ?? 'No name'}
                  </span>
                  <span className="ml-2 font-sans text-xs text-ink/50">@{u.username}</span>
                </span>
                <span className="flex shrink-0 items-center gap-2">
                  {u.suspended_at && <StatusBadge status="suspended" />}
                  <StatusBadge status={u.role} />
                  {/* Surface the half-state: role=creator without approval routes
                      the user as a reader (no creator dashboard/studio). */}
                  {u.role === 'creator' && u.creator_status !== 'approved' && (
                    <span className="rounded-sm bg-oxblood/10 px-2 py-0.5 font-sans text-[0.7rem] uppercase tracking-[0.12em] text-oxblood">
                      not approved
                    </span>
                  )}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <Drawer
        open={Boolean(active)}
        onClose={() => setActive(null)}
        title={active?.display_name ?? 'User'}
      >
        {active && (
          <UserDetail
            user={active}
            onChanged={() => {
              invalidate();
              setActive(null);
            }}
          />
        )}
      </Drawer>
    </div>
  );
}

function UserDetail({ user, onChanged }: { user: Profile; onChanged: () => void }) {
  const [role, setRole] = useState(user.role);
  const [creatorStatus, setCreatorStatus] = useState(user.creator_status);
  const [confirmEmail, setConfirmEmail] = useState('');

  // Keep role and creator_status in lockstep, mirroring apply_creator_decision:
  // a working creator needs BOTH role='creator' AND creator_status='approved'
  // (otherwise sign-in routes them as a reader). Setting role here picks a sane
  // default for creator_status so "make them a creator" actually grants access;
  // the admin can still override the status dropdown afterward (e.g. suspended).
  const onRoleChange = (next: string) => {
    setRole(next);
    if (next === 'creator') {
      if (creatorStatus !== 'approved' && creatorStatus !== 'suspended') {
        setCreatorStatus('approved');
      }
    } else if (creatorStatus !== 'none') {
      setCreatorStatus('none');
    }
  };
  const [username, setUsername] = useState(user.username ?? '');
  const [displayName, setDisplayName] = useState(user.display_name ?? '');
  const [email, setEmail] = useState('');
  const queryClient = useQueryClient();

  const emailQ = useQuery({
    queryKey: ['admin', 'user-email', user.id],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('admin_user_email', { target_id: user.id });
      if (error) throw error;
      return data as string | null;
    },
  });
  const audit = useQuery(adminQueries.userAudit(user.id));
  const sub = useQuery({
    queryKey: ['admin', 'user-sub', user.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('subscriptions')
        .select('status, tier')
        .eq('user_id', user.id)
        .maybeSingle();
      return data;
    },
  });

  // Prefill the editable identity fields; reset when a different user is opened.
  useEffect(() => {
    setUsername(user.username ?? '');
  }, [user.id, user.username]);
  useEffect(() => {
    setDisplayName(user.display_name ?? '');
  }, [user.id, user.display_name]);
  useEffect(() => {
    if (typeof emailQ.data === 'string') setEmail(emailQ.data);
  }, [emailQ.data]);

  const saveUsername = useMutation({
    mutationFn: async () => {
      const next = username.trim().toLowerCase();
      if (!ADMIN_USERNAME_RE.test(next)) {
        throw new Error('3–30 chars: lowercase letters, numbers, _ or -; start alphanumeric');
      }
      if (next !== (user.username ?? '')) {
        const { data: available, error: checkError } = await supabase.rpc(
          'check_username_available',
          { check_username: next },
        );
        if (checkError) throw checkError;
        if (!available) throw new Error('That username is taken');
      }
      const { error } = await supabase
        .from('profiles')
        .update({ username: next })
        .eq('id', user.id);
      if (error) {
        if (error.code === '23505') throw new Error('That username is taken');
        throw error;
      }
      await auditLog('user.username_changed', 'profiles', user.id, { username: next });
    },
    onSuccess: () => {
      toast.success('Username updated.');
      onChanged();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Failed.'),
  });

  const saveDisplayName = useMutation({
    mutationFn: async () => {
      const next = displayName.trim();
      if (!next) throw new Error('Display name is required.');
      // Goes through the admin edge function so the auth user's metadata and the
      // profile stay in sync (a browser client can't write another user's auth).
      const { error } = await supabase.functions.invoke('admin-update-display-name', {
        body: { user_id: user.id, display_name: next },
      });
      if (error) {
        let msg = 'Display name update failed';
        const ctx = (error as { context?: unknown }).context;
        if (ctx instanceof Response) {
          try {
            const b = (await ctx.json()) as { error?: string };
            if (b.error) msg = b.error;
          } catch {
            /* keep default */
          }
        }
        throw new Error(msg);
      }
    },
    onSuccess: () => {
      toast.success('Display name updated.');
      onChanged();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Failed.'),
  });

  const saveEmail = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.functions.invoke('admin-update-email', {
        body: { user_id: user.id, email: email.trim() },
      });
      if (error) {
        let msg = 'Email update failed';
        const ctx = (error as { context?: unknown }).context;
        if (ctx instanceof Response) {
          try {
            const b = (await ctx.json()) as { error?: string };
            if (b.error) msg = b.error;
          } catch {
            /* keep default */
          }
        }
        throw new Error(msg);
      }
    },
    onSuccess: () => {
      toast.success('Email updated.');
      void queryClient.invalidateQueries({ queryKey: ['admin', 'user-email', user.id] });
      onChanged();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Failed.'),
  });

  const saveRoles = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('profiles')
        .update({ role, creator_status: creatorStatus })
        .eq('id', user.id);
      if (error) throw error;
      await auditLog('user.role_changed', 'profiles', user.id, {
        role,
        creator_status: creatorStatus,
      });
    },
    onSuccess: () => {
      toast.success('Roles updated.');
      onChanged();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Failed.'),
  });

  const [suspendReason, setSuspendReason] = useState('');
  const suspend = useMutation({
    mutationFn: async (suspendNow: boolean) => {
      const { error } = await supabase
        .from('profiles')
        .update({
          suspended_at: suspendNow ? new Date().toISOString() : null,
          // Optional reason; empty falls back to the default. The suspended user
          // sees this on /suspended.
          suspended_reason: suspendNow
            ? suspendReason.trim() || 'Suspended by an administrator.'
            : null,
        })
        .eq('id', user.id);
      if (error) throw error;
      await auditLog(suspendNow ? 'user.suspended' : 'user.unsuspended', 'profiles', user.id, {});
    },
    onSuccess: () => {
      toast.success('Suspension updated.');
      onChanged();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Failed.'),
  });

  const del = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('admin-delete-user', {
        body: { user_id: user.id, confirm_email: confirmEmail.trim() },
      });
      if (error) throw error;
      if ((data as { deleted?: boolean })?.deleted !== true) {
        throw new Error('Deletion did not complete.');
      }
    },
    onSuccess: () => {
      toast.success('Account deleted.');
      onChanged();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Delete failed.'),
  });

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <div>
          <label
            htmlFor="admin-display-name"
            className="mb-1 block font-sans text-xs uppercase tracking-[0.12em] text-ink/60"
          >
            Display name
          </label>
          <div className="flex gap-2">
            <input
              id="admin-display-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="min-w-0 flex-1 rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none"
            />
            <button
              type="button"
              onClick={() => saveDisplayName.mutate()}
              disabled={saveDisplayName.isPending || !displayName.trim()}
              className="shrink-0 rounded-md border border-ink/20 px-3 py-2 text-xs font-medium uppercase tracking-[0.12em] text-ink hover:border-oxblood hover:text-oxblood disabled:opacity-60"
            >
              {saveDisplayName.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
        <div>
          <label
            htmlFor="admin-username"
            className="mb-1 block font-sans text-xs uppercase tracking-[0.12em] text-ink/60"
          >
            Username
          </label>
          <div className="flex gap-2">
            <input
              id="admin-username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              className="min-w-0 flex-1 rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none"
            />
            <button
              type="button"
              onClick={() => saveUsername.mutate()}
              disabled={saveUsername.isPending}
              className="shrink-0 rounded-md border border-ink/20 px-3 py-2 text-xs font-medium uppercase tracking-[0.12em] text-ink hover:border-oxblood hover:text-oxblood disabled:opacity-60"
            >
              {saveUsername.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
        <div>
          <label
            htmlFor="admin-email"
            className="mb-1 block font-sans text-xs uppercase tracking-[0.12em] text-ink/60"
          >
            Email
          </label>
          <div className="flex gap-2">
            <input
              id="admin-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={emailQ.isLoading ? '…' : ''}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              className="min-w-0 flex-1 rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none"
            />
            <button
              type="button"
              onClick={() => saveEmail.mutate()}
              disabled={saveEmail.isPending || !email.trim()}
              className="shrink-0 rounded-md border border-ink/20 px-3 py-2 text-xs font-medium uppercase tracking-[0.12em] text-ink hover:border-oxblood hover:text-oxblood disabled:opacity-60"
            >
              {saveEmail.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
      {user.bio && (
        <Field label="Bio">
          <span className="whitespace-pre-wrap">{user.bio}</span>
        </Field>
      )}
      <Field label="Subscription">
        {sub.data ? `${sub.data.status} · ${sub.data.tier}` : 'None'}
      </Field>
      {user.suspended_at && (
        <div className="rounded-md border border-oxblood/30 bg-oxblood/5 p-3 font-sans text-sm text-oxblood">
          Suspended {new Date(user.suspended_at).toLocaleDateString()} — {user.suspended_reason}
        </div>
      )}

      <div className="space-y-3 border-t border-ink/10 pt-5">
        <p className="font-sans text-xs uppercase tracking-[0.12em] text-ink/50">Roles</p>
        {role === 'creator' && creatorStatus !== 'approved' && (
          <div className="rounded-md border border-oxblood/30 bg-oxblood/5 p-3 font-sans text-xs text-oxblood">
            Role is <strong>creator</strong> but creator status is <strong>{creatorStatus}</strong>{' '}
            — this user is routed as a reader and can&apos;t open the creator dashboard or studio
            until status is <strong>approved</strong>.
          </div>
        )}
        <div className="flex flex-wrap gap-3">
          <select
            value={role}
            onChange={(e) => onRoleChange(e.target.value)}
            className="rounded-md border border-ink/20 bg-parchment px-3 py-2 font-sans text-sm text-ink"
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
          <select
            value={creatorStatus}
            onChange={(e) => setCreatorStatus(e.target.value)}
            className="rounded-md border border-ink/20 bg-parchment px-3 py-2 font-sans text-sm text-ink"
          >
            {CREATOR_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <button
            type="button"
            disabled={saveRoles.isPending}
            onClick={() => saveRoles.mutate()}
            className="rounded-md bg-ink px-4 py-2 font-sans text-sm font-medium text-parchment hover:bg-oxblood disabled:opacity-60"
          >
            Save
          </button>
        </div>
      </div>

      <div className="border-t border-ink/10 pt-5">
        {!user.suspended_at && (
          <textarea
            value={suspendReason}
            onChange={(e) => setSuspendReason(e.target.value)}
            rows={2}
            placeholder="Reason (optional) — shown to the suspended user"
            className="mb-3 w-full rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none"
          />
        )}
        <button
          type="button"
          disabled={suspend.isPending}
          onClick={() => suspend.mutate(!user.suspended_at)}
          className="rounded-md border border-ink/30 px-4 py-2 font-sans text-sm font-medium text-ink hover:border-oxblood disabled:opacity-60"
        >
          {user.suspended_at ? 'Lift suspension' : 'Suspend account'}
        </button>
      </div>

      <div className="space-y-2 border-t border-oxblood/20 pt-5">
        <p className="font-sans text-xs uppercase tracking-[0.12em] text-oxblood">Delete account</p>
        <p className="font-sans text-xs text-ink/60">
          Irreversible. Cancels any active subscription and cascades through the database. Type the
          user’s email to confirm.
        </p>
        <input
          type="text"
          placeholder={emailQ.data ?? 'user email'}
          value={confirmEmail}
          onChange={(e) => setConfirmEmail(e.target.value)}
          className="w-full rounded-md border border-oxblood/30 bg-parchment px-3 py-2 font-sans text-sm text-ink focus:border-oxblood focus:outline-none"
        />
        <button
          type="button"
          disabled={del.isPending || !confirmEmail.trim()}
          onClick={() => del.mutate()}
          className="rounded-md border border-oxblood px-4 py-2 font-sans text-sm font-medium text-oxblood hover:bg-oxblood/10 disabled:opacity-60"
        >
          Permanently delete
        </button>
      </div>

      <div className="border-t border-ink/10 pt-5">
        <p className="font-sans text-xs uppercase tracking-[0.12em] text-ink/50">Recent activity</p>
        <ul className="mt-2 space-y-1 font-sans text-xs text-ink/60">
          {(audit.data ?? []).map((a) => (
            <li key={a.id}>
              {new Date(a.created_at).toLocaleString()} — {a.action}
            </li>
          ))}
          {(audit.data ?? []).length === 0 && <li>No recorded activity.</li>}
        </ul>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-1 font-sans text-xs uppercase tracking-[0.12em] text-ink/50">{label}</p>
      <div className="font-sans text-sm text-ink/80">{children}</div>
    </div>
  );
}
